import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button, Chip, Dialog, Select, TextField } from '@nyce/admin-kit/ui';
import { ErrorBox, TableScroller } from '../../../components/common';
import {
  monitorsFor,
  type BuddySystemRef,
  type BuddyTenantRef,
  type MonitorGenerateResponse,
} from '../../api/monitors';
import { describeCron } from './cron';
import { fromDraft, type MonitorForm } from './form';

/**
 * "Generate with AI": a description → buddy3 drafts the monitor against one system's schema and validates its sources
 * → the operator reviews warnings and validation, then opens the draft in the editor (nothing is saved here).
 */
export function GenerateDialog({
  target,
  tenants,
  systems,
  onOpen,
  onClose,
}: {
  target: string;
  tenants: BuddyTenantRef[];
  systems: BuddySystemRef[];
  onOpen: (form: MonitorForm) => void;
  onClose: () => void;
}) {
  const [description, setDescription] = useState('');
  const [systemId, setSystemId] = useState(systems[0]?.id ?? '');
  const [tenantId, setTenantId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MonitorGenerateResponse | null>(null);
  const tenantOfSystem = systems.find((s) => s.id === systemId)?.tenantId;
  const generate = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(
        await monitorsFor(target).generate({
          description: description.trim(),
          systemId,
          tenantId: tenantId || undefined,
        })
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const d = result?.draft;
  return (
    <Dialog
      open
      onClose={onClose}
      title="Generate a monitor with AI"
      maxWidth={780}
      footer={
        <div className="bd-toolbar">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={result ? 'ghost' : 'primary'}
            icon={<Sparkles size={14} />}
            onClick={() => void generate()}
            loading={busy}
            disabled={!description.trim() || !systemId}
          >
            {result ? 'Generate again' : 'Generate'}
          </Button>
          {result && (
            <Button onClick={() => onOpen(fromDraft(result.draft, tenantId ? { tenantId } : { systemId }))}>
              Open in editor
            </Button>
          )}
        </div>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <div className="nyk-span2">
          <TextField
            label="What to watch, how often, and who to tell"
            multiline
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Every morning at 06:00, list customer orders older than 2 days that are still not picked, and mail ops@example.com"
          />
        </div>
        <Select
          label="System (schema and validation)"
          value={systemId}
          onChange={(v) => {
            setSystemId(v);
            setTenantId('');
          }}
          options={systems.map((s) => ({ value: s.id, label: `${s.name} (${s.id})` }))}
          placeholder="Select system…"
        />
        <Select
          label="Scope"
          value={tenantId}
          onChange={setTenantId}
          options={[
            { value: '', label: 'This system only' },
            ...tenants
              .filter((t) => t.id === tenantOfSystem)
              .map((t) => ({ value: t.id, label: `Every system of ${t.name}` })),
          ]}
        />
      </div>
      {result && d && (
        <section data-testid="generate-result">
          <div className="nyk-card-head">
            <h3 className="bm-section-title">{d.name || 'Draft'}</h3>
            <span className="nyk-ui-hint">
              {[
                result.model,
                result.attempts && `${result.attempts} attempt(s)`,
                result.calls && `${result.calls} call(s)`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          {d.description && <p className="bm-desc">{d.description}</p>}
          <p className="nyk-ui-hint">
            {d.cronExpression ? describeCron(d.cronExpression) : 'no schedule'} · {d.runMode ?? 'agent'} ·{' '}
            {(d.sources ?? []).length} source(s)
          </p>
          {result.warnings?.length > 0 && (
            <ul className="bm-warnings">
              {result.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
          {result.validation?.length > 0 && (
            <TableScroller>
              <table className="nyk-ui-table nyk-cards">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Result</th>
                    <th>Columns</th>
                  </tr>
                </thead>
                <tbody>
                  {result.validation.map((v) => (
                    <tr key={v.label} data-validation={v.label}>
                      <td className="nyk-card-title">
                        {v.label} <Chip size="sm">{v.type}</Chip>
                      </td>
                      <td data-label="Result">
                        <Chip size="sm" className={v.success ? 'nyk-tone-green' : 'nyk-tone-red'}>
                          {v.success ? `${v.rowCount} rows` : 'failed'}
                        </Chip>
                        {v.error && <div className="bm-error">{v.error}</div>}
                        {v.warnings?.map((w, i) => (
                          <div key={i} className="nyk-ui-hint">
                            {w}
                          </div>
                        ))}
                      </td>
                      <td data-label="Columns" className="nyk-ui-hint">
                        {v.columns?.join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          )}
          {!!d.assumptions?.length && (
            <div className="bm-notice">
              <b>Assumptions</b>
              <ul>
                {d.assumptions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </Dialog>
  );
}
